import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { DifficultySelector, GameOverScreen } from "./GameShell";
import { toast } from "sonner";

const DIFFICULTY_CONFIG = {
  easy:   { startLen: 4, showTime: 550, hideTime: 350, colors: 5, bonusMult: 1 },
  medium: { startLen: 5, showTime: 400, hideTime: 250, colors: 5, bonusMult: 1.5 },
  hard:   { startLen: 6, showTime: 280, hideTime: 180, colors: 6, bonusMult: 2 },
};

const ALL_COLORS = ["red", "blue", "green", "yellow", "purple", "cyan"];
const COLOR_CLASSES = {
  red:    "bg-red-500 hover:bg-red-600",
  blue:   "bg-blue-500 hover:bg-blue-600",
  green:  "bg-green-500 hover:bg-green-600",
  yellow: "bg-yellow-500 hover:bg-yellow-600",
  purple: "bg-purple-500 hover:bg-purple-600",
  cyan:   "bg-cyan-500 hover:bg-cyan-600",
};
const ACTIVE_CLASSES = {
  red:    "bg-red-300 scale-110 ring-4 ring-red-200",
  blue:   "bg-blue-300 scale-110 ring-4 ring-blue-200",
  green:  "bg-green-300 scale-110 ring-4 ring-green-200",
  yellow: "bg-yellow-300 scale-110 ring-4 ring-yellow-200",
  purple: "bg-purple-300 scale-110 ring-4 ring-purple-200",
  cyan:   "bg-cyan-300 scale-110 ring-4 ring-cyan-200",
};

const TUTORIAL_STEPS = [
  { visual: "🎨", title: "Watch the Colors", description: "A sequence of colored tiles will light up one by one. Pay close attention to the order!" },
  { visual: "🧠", title: "Remember the Order", description: "After the sequence finishes, tap the tiles in the exact same order you saw them." },
  { visual: "📈", title: "Levels Get Harder", description: "Each level adds more tiles to the sequence. How far can you go?" },
];

const MemorySequenceGame = ({ onComplete, onBack, t }) => {
  const [difficulty, setDifficulty] = useState(null);
  const [sequence, setSequence] = useState([]);
  const [userSequence, setUserSequence] = useState([]);
  const [isShowing, setIsShowing] = useState(false);
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [showingIndex, setShowingIndex] = useState(-1);
  const [config, setConfig] = useState(null);

  const getColors = useCallback(() => {
    if (!config) return ALL_COLORS.slice(0, 4);
    return ALL_COLORS.slice(0, config.colors);
  }, [config]);

  const showSequence = useCallback(async (seq, cfg) => {
    setIsShowing(true);
    for (let i = 0; i < seq.length; i++) {
      await new Promise((r) => setTimeout(r, cfg.showTime));
      setShowingIndex(i);
      await new Promise((r) => setTimeout(r, cfg.hideTime));
      setShowingIndex(-1);
    }
    setIsShowing(false);
  }, []);

  const startLevel = useCallback((lvl, cfg) => {
    const colors = ALL_COLORS.slice(0, cfg.colors);
    const seqLen = cfg.startLen + lvl - 1;
    const newSeq = Array.from({ length: seqLen }, () =>
      colors[Math.floor(Math.random() * colors.length)]
    );
    setSequence(newSeq);
    setUserSequence([]);
    showSequence(newSeq, cfg);
  }, [showSequence]);

  const startGame = useCallback((diff) => {
    const cfg = DIFFICULTY_CONFIG[diff];
    setDifficulty(diff);
    setConfig(cfg);
    setLevel(1);
    setScore(0);
    setGameOver(false);
    startLevel(1, cfg);
  }, [startLevel]);

  const handleColorClick = (color) => {
    if (isShowing || gameOver) return;
    const newUser = [...userSequence, color];
    setUserSequence(newUser);

    const idx = newUser.length - 1;
    if (sequence[idx] !== color) {
      setGameOver(true);
      onComplete(score, level, null, difficulty);
      return;
    }

    if (newUser.length === sequence.length) {
      const levelScore = Math.round(level * 10 * config.bonusMult);
      setScore((s) => s + levelScore);
      setLevel((l) => l + 1);
      toast.success(t("games.levelComplete", { level }));
      setTimeout(() => startLevel(level + 1, config), 800);
    }
  };

  if (!difficulty) {
    return (
      <DifficultySelector
        gameName={t("games.memorySequence.name")}
        gameDescription={t("games.memorySequence.description")}
        tutorialSteps={TUTORIAL_STEPS}
        onStart={startGame}
        onBack={onBack}
        t={t}
      />
    );
  }

  if (gameOver) {
    return (
      <GameOverScreen
        score={score}
        subtitle={`${t("common.level")} ${level} | ${t("games.difficulty." + difficulty)}`}
        onPlayAgain={() => startGame(difficulty)}
        onBack={onBack}
        t={t}
      />
    );
  }

  const colors = getColors();
  const gridCols = colors.length <= 4 ? 2 : 3;

  return (
    <div className="text-center" data-testid="memory-game-active">
      <div className="mb-6">
        <p className="text-sm text-slate-500">{t("common.level")} {level} &middot; {t("games.difficulty." + difficulty)}</p>
        <p className="text-2xl font-light text-sky-900" style={{ fontFamily: "Outfit" }}>
          {t("common.score")}: {score}
        </p>
      </div>

      {isShowing && <p className="text-sky-500 mb-4 animate-pulse">{t("games.watchSequence")}</p>}
      {!isShowing && !gameOver && (
        <p className="text-slate-600 mb-4">{t("games.repeatSequence")} ({userSequence.length}/{sequence.length})</p>
      )}

      <div
        className="grid gap-4 max-w-xs mx-auto mb-6"
        style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}
      >
        {colors.map((color) => (
          <button
            key={color}
            onClick={() => handleColorClick(color)}
            disabled={isShowing || gameOver}
            data-testid={`color-${color}`}
            className={`
              h-20 rounded-xl transition-all duration-200
              ${showingIndex >= 0 && sequence[showingIndex] === color
                ? ACTIVE_CLASSES[color]
                : COLOR_CLASSES[color]}
              ${isShowing || gameOver ? "cursor-not-allowed opacity-70" : "cursor-pointer active:scale-95"}
            `}
          />
        ))}
      </div>
    </div>
  );
};

export default MemorySequenceGame;
